import React, { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';

import Input from 'components/partials/input/input';
import Select from 'components/partials/select/select';
import Textarea from 'components/partials/textarea/textarea';
import RadioInput from 'components/partials/radio-input/radio-input';
import Checkbox from 'components/partials/checkbox/checkbox';

import { formatDate } from 'helpers/dates-formatter';

import {
  AID_MODEL_DESCRIPTION_MAX_LENGTH,
  AID_MODEL_NAME_MAX_LENGTH,
  DATASET_DATE_FORMAT,
  DATASET_STATUSES,
  DEFAULT_ENTRY_TERM,
  ENROLLMENT_TYPES,
} from '../../aid-studio-details.constants';

interface SourceTableOption {
  value: string;
  label: string;
}

interface DatasetData {
  status?: string;
  datasetTableCreatedDate?: string;
  lastRecreatedDate?: string;
  sourceTableName?: string;
}

interface BasicInfoStepProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  sourceTablesOptions: SourceTableOption[];
  sourceTablesLoading: boolean;
  sourceTablesFetching: boolean;
  isDirty: boolean;
  isValid: boolean;
  onSave: () => void;
  isNewModel: boolean;
  recreateDataset: boolean;
  onRecreateDatasetChange: (checked: boolean) => void;
  datasetData?: DatasetData;
  isUserView?: boolean;
  readOnly: boolean;
}

const BasicInfoStep = forwardRef<unknown, BasicInfoStepProps>(
  (
    {
      control,
      errors,
      sourceTablesOptions,
      sourceTablesLoading,
      sourceTablesFetching,
      isDirty,
      isValid,
      onSave,
      isNewModel,
      recreateDataset,
      onRecreateDatasetChange,
      datasetData,
      isUserView,
      readOnly,
    },
    ref
  ) => {
    useImperativeHandle(
      ref,
      () => ({
        hasChanges: (isDirty || recreateDataset) && isValid,
        save: onSave,
      }),
      [isDirty, isValid, onSave, recreateDataset]
    );

    const isDatasetInProgress =
      datasetData?.status === DATASET_STATUSES.IN_PROGRESS;
    const showDatasetFields = !isUserView;

    return (
      <div
        className="relative flex gap-8"
        data-testid="basic-info-step"
        inert={readOnly}
      >
        <div className="flex flex-col w-1/2 gap-4">
          {isNewModel && (
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Model name is required',
                maxLength: {
                  value: AID_MODEL_NAME_MAX_LENGTH,
                  message: `Model name cannot exceed ${AID_MODEL_NAME_MAX_LENGTH} characters`,
                },
              }}
              render={({ field }) => (
                <Input
                  label="Model name"
                  labelFor="name"
                  labelInTop
                  required
                  errorsNames={errors.name as never}
                  inputCustomClass="h-10"
                  customClass="mt-0 text-md"
                  placeholder="Enter model name"
                  data-testid="model-name-input"
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                />
              )}
            />
          )}

          <div className="flex gap-6">
            <div className="w-1/2">
              <Controller
                name="entryTerm"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Entry term year"
                    labelFor="entryTerm"
                    labelInTop
                    disabled
                    fieldError={errors.entryTerm}
                    options={DEFAULT_ENTRY_TERM}
                    customClass="mt-0 text-md"
                    extraBasicStyles={{ height: 40, padding: 0 }}
                    data-testid="entry-term-select"
                    {...field}
                    value={DEFAULT_ENTRY_TERM.find(
                      (opt) => opt.value === field.value
                    )}
                    onChange={(option) => {
                      if (option && !Array.isArray(option)) {
                        field.onChange(option.value);
                      }
                    }}
                  />
                )}
              />
            </div>

            <div className="w-1/2">
              <span className="font-semibold flex mb-2">Enrollment type</span>
              <Controller
                name="enrollmentType"
                control={control}
                rules={{ required: 'Enrollment type is required' }}
                render={({ field }) => (
                  <div className="flex items-center pt-1 gap-5">
                    {ENROLLMENT_TYPES.map(({ value, label }) => (
                      <RadioInput
                        key={value}
                        label={label}
                        labelFor={`enrollment-${value}`}
                        radioValue={value}
                        customClass="m-0"
                        labelClass="whitespace-nowrap text-md"
                        customInputClasses="size-5"
                        data-testid={`enrollment-type-${value}`}
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        setAction={field.onChange}
                      />
                    ))}
                  </div>
                )}
              />
              {errors.enrollmentType && (
                <span className="text-sm text-critical-600">
                  {errors.enrollmentType.message}
                </span>
              )}
            </div>
          </div>

          {showDatasetFields && (
            <div>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Controller
                    name="sourceTableName"
                    control={control}
                    rules={{ required: 'Data science table is required' }}
                    render={({ field }) => (
                      <Select<SourceTableOption>
                        label="Data science table"
                        labelFor="sourceTableName"
                        labelInTop
                        required
                        disabled={
                          sourceTablesLoading ||
                          (!isNewModel && !recreateDataset)
                        }
                        fieldError={errors.sourceTableName as never}
                        options={sourceTablesOptions}
                        isLoading={sourceTablesLoading || sourceTablesFetching}
                        placeholder={
                          sourceTablesLoading ? 'Loading...' : 'Select table'
                        }
                        data-testid="source-table-select"
                        customClass="mt-0 text-md"
                        extraBasicStyles={{ height: 40, padding: 0 }}
                        {...field}
                        value={sourceTablesOptions?.find(
                          (opt) => opt.value === field.value
                        )}
                        onChange={(option) => {
                          if (option && !Array.isArray(option)) {
                            field.onChange(option.value);
                          }
                        }}
                      />
                    )}
                  />
                </div>

                {!isNewModel && (
                  <Checkbox
                    labelFor="recreate-dataset"
                    label="Recreate dataset"
                    checked={recreateDataset}
                    onChange={({ hasAccess: isChecked }) =>
                      onRecreateDatasetChange(isChecked)
                    }
                    disabled={isDatasetInProgress}
                    customStylesLabel="pb-2 select-none font-semibold text-gray-700"
                    inputCustomClass="size-5"
                    labelTextClass="text-md"
                  />
                )}
              </div>

              {!isNewModel && datasetData?.lastRecreatedDate && (
                <p
                  className="text-xs font-semibold text-gray-700 mt-1"
                  data-testid="last-recreated-date"
                >
                  Last recreated:{' '}
                  {formatDate(
                    datasetData.lastRecreatedDate,
                    DATASET_DATE_FORMAT
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col w-1/2">
          <Controller
            name="description"
            control={control}
            rules={{
              maxLength: {
                value: AID_MODEL_DESCRIPTION_MAX_LENGTH,
                message: `Description cannot exceed ${AID_MODEL_DESCRIPTION_MAX_LENGTH} characters`,
              },
            }}
            render={({ field }) => (
              <Textarea
                label="Description (optional)"
                labelFor="description"
                customLabelStyles="mb-1"
                textareaStyles="h-[208px]"
                maxLength={AID_MODEL_DESCRIPTION_MAX_LENGTH}
                rows={8}
                showCharCount={false}
                placeholder="Enter model description"
                errorsNames={(errors.description || {}) as never}
                data-testid="model-description-textarea"
                {...field}
                value={(field.value as string | undefined) ?? ''}
              />
            )}
          />
        </div>
      </div>
    );
  }
);

BasicInfoStep.displayName = 'BasicInfoStep';

export default BasicInfoStep;
